import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core';
import MenuItem from '@material-ui/core/MenuItem';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Grid from '@material-ui/core/Grid';
import Divider from '@material-ui/core/Divider';
import Hidden from '@material-ui/core/Hidden';
import Button from '@material-ui/core/Button';
import LinearProgress from '@material-ui/core/LinearProgress';
import { Field, reduxForm } from 'redux-form';

import ORTypography from 'components/ORTypography/ORTypography';
import NodeList from 'components/NodeList/NodeList';
import SERVICES_WITH_NODE_SCALING from 'constants/servicesWithNodeScaling';
import WrappedTextField from 'components/WrappedTextField/WrappedTextField';
import { bytesToGB } from 'services/conversions';

const propTypes = {
  spaceUsage: PropTypes.shape({
    nodes: PropTypes.instanceOf(Array),
    spaceUsed: PropTypes.number.isRequired,
  }),
  classes: PropTypes.shape({}).isRequired,
  region: PropTypes.string.isRequired,
  hosts: PropTypes.arrayOf(
    PropTypes.shape({
      memoryMb: PropTypes.number,
      role: PropTypes.string,
    }),
  ).isRequired,
  id: PropTypes.string.isRequired,
  service: PropTypes.string.isRequired,
  statusDisplay: PropTypes.string.isRequired,
  userRoles: PropTypes.arrayOf(PropTypes.string).isRequired,
  changeNodeQuantity: PropTypes.func.isRequired,
};

const defaultProps = {
  spaceUsage: {
    nodes: [],
  },
};

export function UnstyledNodesTab({
  classes,
  region,
  hosts,
  id,
  service,
  statusDisplay,
  userRoles,
  changeNodeQuantity,
  spaceUsage,
}) {
  const physicalHosts = hosts.filter(
    (host) => host.diskSizeGb !== 0 && host.role !== 'cockroachdb_admin',
  );
  const [selectedHost, selectHost] = React.useState(physicalHosts[0] ? physicalHosts[0].role : 0);
  const [displayHost, toggleDisplayHost] = React.useState(true);
  const [selectedNodeQuantity, selectNodeQuantity] = React.useState(0);

  const handleSelection = (event) => {
    selectHost(event.target.value);
    selectNodeQuantity(0);
  };

  const handleConfirm = () => {
    changeNodeQuantity(service, id, selectedNodeQuantity);
    selectNodeQuantity(0);
  };

  const showDedicatedMasterOption =
    service === 'Elasticsearch' && !physicalHosts.some((host) => host.role === 'dedicated_master');

  let displayHosts = physicalHosts.filter((host) => host.role === selectedHost);

  if (showDedicatedMasterOption && selectedHost === 'dedicated_master') {
    displayHosts = [
      {
        diskSizeGb: 0,
        quantity: 0,
        memoryMb: 0,
        role: 'dedicated_master',
      },
    ];
  }

  const dedicatedMasterOption = (
    <MenuItem value="dedicated_master">Add Dedicated Master Nodes</MenuItem>
  );

  const displayRole = (role) =>
    role
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

  const hostOptions = physicalHosts.map((host) => {
    const displayText = `${host.quantity} Node${host.quantity > 1 ? 's' : ''}, ${
      host.diskSizeGb
    }GB Storage/${host.memoryMb}MB Memory each`;
    return (
      <MenuItem value={host.role} key={host.id}>
        {displayRole(host.role)}
        <Hidden mdUp>
          <br />
          {displayHost && displayText}
        </Hidden>
        <Hidden smDown>{displayHost && `: ${displayText}`}</Hidden>
      </MenuItem>
    );
  });

  let addNodeQuantityRemainder;
  let addNodeQuantityOptions;
  let defaultAddNodeMenuOption;
  const validHost =
    SERVICES_WITH_NODE_SCALING[service] && SERVICES_WITH_NODE_SCALING[service][selectedHost]; // eslint-disable-line max-len
  const selectedHostMaxQuantity = validHost
    ? SERVICES_WITH_NODE_SCALING[service][selectedHost].maxQuantity
    : 0; // eslint-disable-line max-len

  const validDedicatedMasterAddonOptions = [
    {
      desiredQuantity: 3,
      role: selectedHost,
      desiredPerNodeMemoryMb: 2048,
      desiredPerNodeDiskSizeGb: 8,
    },
    {
      desiredQuantity: 3,
      role: selectedHost,
      desiredPerNodeMemoryMb: 4096,
      desiredPerNodeDiskSizeGb: 16,
    },
    {
      desiredQuantity: 3,
      role: selectedHost,
      desiredPerNodeMemoryMb: 8192,
      desiredPerNodeDiskSizeGb: 32,
    },
  ];

  // this if statement helps services without scaling from breaking the nodes tab
  if (validHost) {
    if (selectedHost === 'dedicated_master') {
      // the option variable here is one of the objects from validDedicatedMasterAddonOptions
      // it needs to be stringified or it comes back as [Object object] and MUI throws an error
      addNodeQuantityOptions = validDedicatedMasterAddonOptions.map((option, index) => (
        <MenuItem value={JSON.stringify(option)} key={index.toString()}>
          3X&nbsp;
          {option.desiredPerNodeMemoryMb / 1024}
          GB RAM/
          {option.desiredPerNodeDiskSizeGb}
          GB Disk
        </MenuItem>
      ));
    } else {
      addNodeQuantityRemainder = [
        ...Array(selectedHostMaxQuantity - displayHosts[0].quantity).keys(),
      ];
      // the option variable here is just a number (0 should be the first number), not an object
      addNodeQuantityOptions = addNodeQuantityRemainder.map((option, index) => (
        <MenuItem
          value={JSON.stringify({
            desiredQuantity: option + 1 + displayHosts[0].quantity,
            role: selectedHost,
          })} // eslint-disable-line max-len
          key={index.toString()}
        >
          {option + 1}
        </MenuItem>
      ));
    }
    if (statusDisplay !== 'Ready') {
      defaultAddNodeMenuOption =
        'Please wait for the cluster to be ready before adding additional nodes.';
    } else if (selectedHost === 'dedicated_master' && showDedicatedMasterOption) {
      defaultAddNodeMenuOption = 'Please select an option for your dedicated master nodes.';
    } else if (selectedHostMaxQuantity > displayHosts[0].quantity) {
      defaultAddNodeMenuOption = 'How many nodes would you like to add?';
    } else if (selectedHostMaxQuantity === displayHosts[0].quantity) {
      defaultAddNodeMenuOption = 'You have reached the maximum number of nodes.';
    }
  }

  const filteredNodesSpaceUsage = () => {
    const reducer = (accumulator, currentValue) => accumulator + currentValue.spaceUsed;
    let spaceUsed = spaceUsage.nodes.reduce(reducer, 0);
    if (service === 'Elasticsearch') {
      spaceUsed = spaceUsage.nodes
        .filter((node) =>
          node.name.includes(
            selectedHost === 'dedicated_master' ? 'es-dedicated-master' : selectedHost,
          ),
        )
        .reduce(reducer, 0);
    }
    return bytesToGB(spaceUsed).toFixed(2);
  };

  return (
    <div className={classes.pageContainer}>
      <div className={classes.titleContainer}>
        <ORTypography component="h2" variant="h5">
          Manage and Scale Nodes
        </ORTypography>
      </div>
      <Divider classes={{ root: classes.divider }} variant="fullWidth" />
      <Grid container classes={{ container: classes.nodeContainer }}>
        <Grid item container xs={12} md={12} classes={{ item: classes.nodePanel }}>
          {physicalHosts.length > 0 && (
            <Field
              name="nodes_class"
              label="Node Class"
              variant="outlined"
              select
              props={{
                InputLabelProps: {
                  shrink: true,
                  className: classes.inputLabel,
                },
              }}
              SelectProps={{
                value: selectedHost,
                onOpen: () => toggleDisplayHost(false),
                onClose: () => toggleDisplayHost(true),
                IconComponent: ExpandMoreIcon,
              }}
              component={WrappedTextField}
              className={classes.formControl}
              fullWidth
              onChange={handleSelection}
            >
              {hostOptions}
              {showDedicatedMasterOption && dedicatedMasterOption}
            </Field>
          )}
          <div className={classes.textPanel}>
            <ORTypography component="span" className={classes.textContent} variant="subtitle1">
              You have&nbsp;
              <strong>
                {displayHosts.length > 0 ? displayHosts[0].quantity : 0}
                &nbsp;
                {displayHosts.length > 0 && displayHosts[0].quantity > 1 ? 'Nodes' : 'Node'}
                &nbsp;with&nbsp;
                {displayHosts.length > 0 ? displayHosts[0].diskSizeGb : 0}
                GB Storage/
                {displayHosts.length > 0 && displayHosts[0].memoryMb}
                MB Memory&nbsp;
              </strong>
              each. You are currently using&nbsp;
              <strong>
                {spaceUsage.nodes.length > 0 ? filteredNodesSpaceUsage(service) : 0}
                GB of&nbsp;
                {/* eslint-disable-next-line max-len */}
                {displayHosts.length > 0
                  ? displayHosts[0].diskSizeGb * displayHosts[0].quantity
                  : 0}
                GB storage.
              </strong>
            </ORTypography>
            {showDedicatedMasterOption && selectedHost === 'dedicated_master' && (
              <React.Fragment>
                <br />
                <ORTypography component="span" className={classes.textContent} variant="subtitle1">
                  <strong>Please add nodes to get started.</strong>
                </ORTypography>
              </React.Fragment>
            )}
          </div>
          <NodeList
            instanceId={id}
            service={service}
            region={region}
            hosts={displayHosts}
            userRoles={userRoles}
            changeNodeQuantity={changeNodeQuantity}
            statusDisplay={statusDisplay}
          />
        </Grid>
        {validHost && (
          <Grid item container xs={12} md={12} classes={{ item: classes.nodePanel }}>
            <ORTypography component="h4" variant="h6">
              Add Nodes
            </ORTypography>
            <div className={classes.textPanel}>
              <ORTypography component="span" className={classes.textContent} variant="subtitle1">
                You can add multiple nodes to scale horizontally and each will have the same storage
                and memory as your current nodes.&nbsp; After confirming,&nbsp;
                {service}
                &nbsp;will utilize the new resources as soon as they connect.
              </ORTypography>
            </div>
            <Field
              name="add_nodes"
              label="Add Nodes"
              placeholder="How many nodes would you like to add?"
              variant="outlined"
              select
              disabled={
                !(selectedHostMaxQuantity > displayHosts[0].quantity) || statusDisplay !== 'Ready'
              }
              props={{
                InputLabelProps: {
                  shrink: true,
                  className: classes.inputLabel,
                },
              }}
              SelectProps={{
                displayEmpty: true,
                value: selectedNodeQuantity,
                IconComponent: ExpandMoreIcon,
              }}
              component={WrappedTextField}
              className={classes.formControl}
              fullWidth
              onChange={(event) => selectNodeQuantity(event.target.value)}
            >
              <MenuItem value="0" disabled>
                {defaultAddNodeMenuOption}
              </MenuItem>
              {addNodeQuantityOptions}
            </Field>
            <div className={classes.buttonContainer}>
              <Button
                className={classes.nodeActionButton}
                variant="contained"
                color="secondary"
                onClick={() => selectNodeQuantity(0)}
                disabled={
                  selectedNodeQuantity < 1 ||
                  selectedHostMaxQuantity === displayHosts[0].quantity ||
                  statusDisplay !== 'Ready'
                }
              >
                Cancel
              </Button>
              <Button
                className={classes.nodeActionButton}
                variant="contained"
                color="primary"
                onClick={handleConfirm}
                disabled={
                  selectedNodeQuantity < 1 ||
                  selectedHostMaxQuantity === displayHosts[0].quantity ||
                  statusDisplay !== 'Ready'
                }
              >
                {(statusDisplay === 'Update Requested' || statusDisplay === 'Updating') && (
                  <LinearProgress
                    classes={{
                      root: classes.progress,
                      colorPrimary: classes.progressPrimary,
                      bar: classes.progressSecondary,
                    }}
                  /> // eslint-disable-line max-len
                )}
                {statusDisplay === 'Update Requested' || statusDisplay === 'Updating'
                  ? 'Building'
                  : 'Confirm and Build'}
              </Button>
            </div>
            <ORTypography
              component="span"
              className={classes.billingTextContent}
              variant="subtitle1"
            >
              <em>
                Any updates made here will also be applied to all Regions and your billing will be
                adjusted.
              </em>
            </ORTypography>
          </Grid>
        )}
        {/* The grid below is a placeholder for content in the very near future */}
        {/* <Grid item container xs={12} md={12} classes={{ item: classes.nodePanel }}>
          <ORTypography component="h4" variant="h6">Scale All Nodes</ORTypography>
          <ORTypography component="span" className={classes.textContent} variant="subtitle1">
            You can scale all of your nodes vertically by selectin new storage and memory&nbsp;
            sizes. This will effect all of your current nodes.
          </ORTypography>
        </Grid> */}
      </Grid>
    </div>
  );
}

const styles = (theme) => ({
  formControl: {
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(1),
  },
  selectRoot: {
    color: theme.palette.secondary.main,
    fontSize: '14px',
  },
  titleContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',

    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  regionTabs: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(3),
    color: theme.customColors.white,
    backgroundColor: theme.customColors.darkPurple,
    boxShadow: `0 ${theme.spacing(1)}px ${theme.spacing(0.5)}px -${theme.spacing(0.5)}px gray`,
  },
  indicator: {
    borderBottom: '2px solid white',
  },
  selectedTab: {
    borderBottom: '2px solid white',
  },
  legendContainer: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  linearLegend: {
    fontSize: theme.typography.caption.fontSize,
  },
  nodeNumber: {
    position: 'absolute',
    top: '40%',
    width: '100%',
    textAlign: 'center',
    fontSize: '6rem',
    fontFamily: '"Libre Franklin Regular", sans-serif',
    zIndex: 10,
  },
  chartContainer: {
    height: '150px',
    border: '1px solid #979797',
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(1.5),

    [theme.breakpoints.down('sm')]: {
      margin: theme.spacing(2),
    },
  },
  chart: {
    height: '85%',
    width: '100%',
    borderBottom: '1px solid #979797',
    position: 'relative',
  },
  chartLegend: {
    fontFamily: '"Libre Franklin Regular", sans-serif',
    fontSize: '14px',
    textAlign: 'center',
  },
  usageBar: {
    borderTop: '1px solid #979797',
    width: '100%',
    position: 'absolute',
    bottom: 0,
    backgroundColor: theme.customColors.sky,
  },
  textContent: {
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
    fontSize: theme.typography.caption.fontSize,
    '& strong': {
      color: theme.customColors.mediumGrey,
    },
  },
  billingTextContent: {
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
    fontSize: theme.typography.caption.fontSize,
    '& em': {
      color: theme.customColors.magenta,
    },
  },
  nodeContainer: {
    margin: 0,
    flexDirection: 'column',

    [theme.breakpoints.up('md')]: {
      padding: 'unset',
      justifyContent: 'space-between',
      border: 'none',
    },
  },
  nodePanel: {
    alignSelf: 'flex-start',
    flexDirection: 'column',
    paddingBottom: theme.spacing(2),

    [theme.breakpoints.up('md')]: {
      padding: `0 ${theme.spacing(2.5)}px ${theme.spacing(2)}px 0`,
    },
  },
  linearBarRoot: {
    backgroundColor: theme.customColors.lightGrey,
    marginBottom: theme.spacing(2),
  },
  linearBar: {
    backgroundColor: theme.customColors.sky,
  },
  pageContainer: {
    padding: theme.spacing(1),
    backgroundColor: theme.customColors.white,
    border: `1px solid ${theme.customColors.lightGrey}`,
  },
  divider: {
    borderTop: `1px solid ${theme.customColors.mediumGrey}`,
    marginBottom: theme.spacing(2),
    marginRight: '-8px',
    marginLeft: '-8px',
  },
  textPanel: {
    marginBottom: theme.spacing(2),
  },
  inputLabel: {
    color: theme.customColors.black,
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'column-reverse',

    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
    },
  },
  nodeActionButton: {
    padding: '6px 32px',
    margin: `${theme.spacing(1)}px 0`,

    [theme.breakpoints.up('md')]: {
      margin: `${theme.spacing(1)}px ${theme.spacing(1)}px ${theme.spacing(1)}px 0`,
    },
    '&:last-child': {
      minWidth: '191px',
    },
  },
  progress: {
    position: 'absolute',
    clear: 'both',
    left: 0,
    bottom: 0,
    width: '100%',
    borderBottomRightRadius: '4px',
    borderBottomLeftRadius: '4px',
  },
  progressPrimary: {
    backgroundColor: theme.customColors.khaki,
  },
  progressSecondary: {
    backgroundColor: theme.customColors.burntYellow,
  },
});

UnstyledNodesTab.propTypes = propTypes;
UnstyledNodesTab.defaultProps = defaultProps;

export default withStyles(styles)(reduxForm({ form: 'addNodes' })(UnstyledNodesTab));
